import * as THREE from 'three';

/**
 * Multi-layer radial speed lines — 3D meshes parented to the camera (additive).
 * Aligns intensity with `#speed-streaks` (same streak01 as GameView) + post radial blur.
 */

/** Same formula as GameView `speed-streaks` opacity ramp. */
function streak01FromSpeed(speed01: number): number {
  return THREE.MathUtils.clamp((speed01 - 0.2) / 0.8, 0, 1);
}

function buildFanGeometry(
  segments: number,
  inner: number,
  outer: number,
  wedge: number,
  rotation: number,
  innerRgb: [number, number, number],
  outerRgb: [number, number, number],
): THREE.BufferGeometry {
  const positions: number[] = [];
  const colors: number[] = [];
  const [ir, ig, ib] = innerRgb;
  const [or, og, ob] = outerRgb;
  for (let i = 0; i < segments; i++) {
    const a0 = rotation + (i / segments) * Math.PI * 2;
    const a1 = rotation + ((i + wedge) / segments) * Math.PI * 2;
    const j = 0.92 + (i % 7) * 0.014;
    positions.push(
      Math.cos(a0) * inner,
      Math.sin(a0) * inner,
      0,
      Math.cos(a1) * outer * j,
      Math.sin(a1) * outer * j,
      0,
    );
    colors.push(ir, ig, ib, or, og, ob);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(new Float32Array(positions), 3),
  );
  geo.setAttribute(
    'color',
    new THREE.Float32BufferAttribute(new Float32Array(colors), 3),
  );
  return geo;
}

export function createRadialSpeedLinesMesh(): {
  group: THREE.Group;
  update: (dt: number, speed01: number) => void;
  dispose: () => void;
} {
  const group = new THREE.Group();
  group.name = 'RadialSpeedLines';
  group.renderOrder = 999;
  group.frustumCulled = false;

  const innerGroup = new THREE.Group();
  const outerGroup = new THREE.Group();
  const sparkGroup = new THREE.Group();
  group.add(innerGroup);
  group.add(outerGroup);
  group.add(sparkGroup);

  // Fine inner fan — cyan core
  const geoInner = buildFanGeometry(
    64,
    0.02,
    0.72,
    0.42,
    0,
    [0.35, 0.95, 0.92],
    [0.85, 1, 1],
  );
  const matInner = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
  });
  const linesInner = new THREE.LineSegments(geoInner, matInner);
  innerGroup.add(linesInner);

  // Longer outer streaks — fewer, warmer, counter-rotates for depth
  const geoOuter = buildFanGeometry(
    36,
    0.06,
    1.22,
    0.38,
    0.11,
    [0.55, 0.75, 1],
    [1, 0.92, 0.75],
  );
  const matOuter = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
  });
  const linesOuter = new THREE.LineSegments(geoOuter, matOuter);
  outerGroup.add(linesOuter);

  // Short “spark” ticks near the rim — breaks up uniformity
  const geoSpark = buildFanGeometry(
    48,
    0.55,
    1.08,
    0.12,
    0.04,
    [0.9, 1, 1],
    [1, 1, 0.95],
  );
  const matSpark = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
  });
  const linesSpark = new THREE.LineSegments(geoSpark, matSpark);
  sparkGroup.add(linesSpark);

  const disposables: Array<THREE.BufferGeometry | THREE.Material> = [
    geoInner,
    matInner,
    geoOuter,
    matOuter,
    geoSpark,
    matSpark,
  ];

  let spin = 0;
  let pulse = 0;

  const update = (dt: number, speed01: number): void => {
    const streak = streak01FromSpeed(speed01);
    const show = streak > 0.04;
    const t = THREE.MathUtils.clamp(speed01, 0, 1);

    matInner.opacity = show ? streak * 0.24 : 0;
    matOuter.opacity = show ? streak * 0.18 : 0;
    matSpark.opacity = show ? streak * 0.13 : 0;

    spin += dt * (0.14 + t * 2.05);
    pulse += dt * (2.6 + streak * 4);

    innerGroup.rotation.z = spin;
    outerGroup.rotation.z = -spin * 0.62;
    sparkGroup.rotation.z = spin * 0.33;

    const breathe = 1 + Math.sin(pulse) * 0.04 * streak;
    const s = (0.9 + t * 0.58) * breathe;
    group.scale.setScalar(s);
  };

  const dispose = (): void => {
    for (const x of disposables) x.dispose();
  };

  return { group, update, dispose };
}
